import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User as UserIcon, Mail, Phone } from "lucide-react";

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/customers").then((r) => { setItems(r.data); setLoading(false); });
  }, []);

  const open = async (c) => {
    setActive(c);
    const { data } = await api.get(`/customers/${c.id}`);
    setHistory(data.history || []);
  };

  if (loading) return <div className="text-slate-500">Loading…</div>;

  if (active) {
    return (
      <div className="space-y-5 fade-in">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setActive(null); setHistory([]); }}><ArrowLeft size={18}/></Button>
          <div>
            <h2 className="text-xl font-extrabold text-[#1F3A6B]">{active.name || active.email || active.phone || "Anonymous"}</h2>
            <p className="text-sm text-slate-500">Unified customer profile</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-5 border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Profile</p>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex items-start gap-2"><Mail size={14} className="mt-1 text-slate-400"/><div><dt className="text-slate-500 text-xs">Email</dt><dd className="font-semibold text-[#1F3A6B]">{active.email || "—"}</dd></div></div>
              <div className="flex items-start gap-2"><Phone size={14} className="mt-1 text-slate-400"/><div><dt className="text-slate-500 text-xs">Phone</dt><dd>{active.phone || "—"}</dd></div></div>
              <div className="flex items-start gap-2"><UserIcon size={14} className="mt-1 text-slate-400"/><div><dt className="text-slate-500 text-xs">First seen</dt><dd>{new Date(active.first_seen_at).toLocaleString()}</dd></div></div>
            </dl>
          </Card>
          <Card className="p-5 border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Latest score</p>
            <p className="mt-2 text-4xl font-extrabold text-[#1F3A6B] tabular-nums">{active.last_nps_score ?? "—"}</p>
            <CatBadge cat={active.last_nps_category}/>
          </Card>
          <Card className="p-5 border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Activity</p>
            <p className="mt-2 text-4xl font-extrabold text-[#1F3A6B] tabular-nums">{active.response_count}</p>
            <p className="text-xs text-slate-500">total responses</p>
          </Card>
        </div>

        <Card className="p-5 border-slate-100">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Response history</p>
          {history.length === 0 ? (
            <p className="text-sm text-slate-500 mt-2">No responses linked yet.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {history.map((h) => (
                <li key={h.id} className="border border-slate-100 rounded-md p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-700">{new Date(h.submitted_at).toLocaleString()}</span>
                    <span className="flex items-center gap-2">
                      {h.nps_score !== null && h.nps_score !== undefined && <span className="font-bold tabular-nums">{h.nps_score}</span>}
                      <CatBadge cat={h.nps_category}/>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5 fade-in">
      <p className="text-slate-600">Unified customer profiles built from response data (deduplicated by email or phone).</p>
      {items.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200">
          <p className="font-bold text-[#1F3A6B]">No customers yet</p>
          <p className="text-sm text-slate-500 mt-1">Customers appear here once respondents include an email or phone.</p>
        </Card>
      ) : (
        <Card className="border-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Name</th>
                <th className="text-left px-4 py-3 font-semibold">Email / Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Responses</th>
                <th className="text-left px-4 py-3 font-semibold">Last score</th>
                <th className="text-left px-4 py-3 font-semibold">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`customer-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => open(c)}>
                  <td className="px-4 py-3 font-semibold text-[#1F3A6B]">{c.name || "Anonymous"}</td>
                  <td className="px-4 py-3 text-slate-700">{c.email || c.phone || "—"}</td>
                  <td className="px-4 py-3 tabular-nums">{c.response_count}</td>
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><span className="font-bold tabular-nums">{c.last_nps_score ?? "—"}</span><CatBadge cat={c.last_nps_category}/></div></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(c.last_seen_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function CatBadge({ cat }) {
  if (!cat) return null;
  const map = { promoter: "bg-emerald-100 text-emerald-700", passive: "bg-amber-100 text-amber-700", detractor: "bg-rose-100 text-rose-700" };
  return <Badge className={`${map[cat]} text-[10px] uppercase`}>{cat}</Badge>;
}
